import { useState } from 'react';
import { brand } from '../brand/brand';

/**
 * Invite a friend. SOW §2.7.
 *
 * A SHARE LINK, AND NOTHING MORE. §10.8 excludes a "loyalty & referral layer"
 * from the base build, and the difference matters: this is a link to the shop,
 * with no referral code, no attribution, no reward on either side, and no record
 * that it was sent. Adding any of those turns an included value-add into an
 * excluded feature, and it would be easy to do by accident while "improving" it.
 *
 * NOTHING IS SENT FROM HERE. The Web Share sheet is the operating system's, and
 * the customer picks the app and the recipient themselves — we never see who was
 * invited, which is both the privacy-preserving design and the reason this needs
 * no backend at all.
 */
export function InviteFriend() {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  // The shop's own address, not a per-customer URL. See the note above.
  const url = brand.urls.shop;
  const text = `${brand.name} — buy credit at local places for less than it is worth.`;

  async function share() {
    setFailed(false);

    // navigator.share is the good path: it opens the OS sheet with WhatsApp,
    // Messages and the rest, which is exactly what §2.7 describes. It exists
    // only on mobile and only over HTTPS, so the clipboard is the fallback
    // rather than an afterthought — on a desktop it is the ONLY path.
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: brand.name, text, url });
        return;
      } catch (error) {
        // AbortError means the customer closed the sheet. That is not a failure
        // and must not fall through to copying a link they decided not to send.
        if (error instanceof DOMException && error.name === 'AbortError') return;

        // Anything else — a browser that advertises share and refuses it — falls
        // through to the clipboard.
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 4000);
    } catch {
      // Clipboard access is refused in some browsers without a user gesture the
      // browser recognises, and in a few private modes. The link is on screen
      // below regardless, so there is always something to do.
      setFailed(true);
    }
  }

  return (
    <section className="stack" style={{ gap: 10 }}>
      <h2>Invite a friend</h2>
      <p>
        Send them the shop. They get the same offers you do — there is no code to
        enter and nothing to claim.
      </p>

      <div className="stack-h wrap" style={{ gap: 10 }}>
        <button type="button" className="btn" onClick={() => void share()}>
          Share {brand.name}
        </button>
      </div>

      {/*
        The link in real text, always. It is what makes this work when the share
        sheet is unavailable and the clipboard is refused — the customer can read
        it out or select it by hand.
      */}
      <p className="hint">
        <span className="visually-hidden">The link is </span>
        <code className="invite-url">{url}</code>
      </p>

      {copied && <p className="notice" role="status">Link copied.</p>}
      {failed && (
        <p className="notice" role="status">
          We could not copy it for you — the link is just above.
        </p>
      )}
    </section>
  );
}
